/**
 * Cabinet bulk file import: upload → preview → mapping (needs_user) → confirm → job poll → results.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getBootstrapData } from '@tpct/admin-ui';
import FeatureGateNotice from '@/hub-shared/components/FeatureGateNotice';
import FecDsnUploadResponsibilityNotice from '../shared/FecDsnUploadResponsibilityNotice';
import { getFecDsnImportGateFromBootstrap } from '@/hub-shared/gates/fecDsnImportGate';
import { __ } from '@/hub-shared/utils/i18n';
import { fetchCompanies } from '../utils/api';
import type { Company } from '../types/company';
import { CABINET_INBOX_ARTIFACT_TYPES } from '../constants/companyProfileImport';
import {
  ArtifactTypeRequiredError,
  createFileExtractionJob,
  getFileExtractionJob,
  isJobTerminal,
  postConfirmMapping,
  postInboxPreview,
  type ConfirmMappingItem,
  type ConfirmMappingResult,
  type FileExtractionJobPoll,
  type MappingPreviewEntry,
  uploadInboxArtifact,
} from '../utils/cabinetFileImportApi';
import type { PortalSearchSyncFn } from '@/portal/src/utils/portalUrlState';
import { parsePortalSearchParams } from '@/portal/src/utils/portalUrlState';

type CabinetFileImportWizardProps = {
  open: boolean;
  files: File[];
  bootstrapKey: string;
  onClose: () => void;
  onImported?: () => void;
  /** modal = centered overlay. inline = in-page block (e.g. replaces form below import actions). */
  variant?: 'modal' | 'inline';
  /** Portal SPA: sync cabinet import wizard step ↔ `cabinetImportStep` query. */
  search?: string;
  patchPortalSearch?: PortalSearchSyncFn;
};

type Step = 'upload' | 'type_select' | 'mapping' | 'confirming' | 'job' | 'results' | 'error';

const decodeCabinetImportStepToken = (raw: string): Step | null => {
  switch (raw) {
    case 'upload':
      return 'upload';
    case 'type':
      return 'type_select';
    case 'mapping':
      return 'mapping';
    case 'job':
      return 'job';
    case 'results':
      return 'results';
    case 'error':
      return 'error';
    default:
      return null;
  }
};

const serializeCabinetImportStepUrl = (step: Step): { cabinetImportStep: string | null } | null => {
  switch (step) {
    case 'upload':
      return { cabinetImportStep: null };
    case 'type_select':
      return { cabinetImportStep: 'type' };
    case 'mapping':
      return { cabinetImportStep: 'mapping' };
    case 'confirming':
      return null;
    case 'job':
      return { cabinetImportStep: 'job' };
    case 'results':
      return { cabinetImportStep: 'results' };
    case 'error':
      return { cabinetImportStep: 'error' };
    default:
      return null;
  }
};

type UploadedRow = {
  fileName: string;
  artifactId: number;
};

type Choice = { companyId?: number };

type PerFileStatus = {
  fileName: string;
  artifactId: number;
  preview?: MappingPreviewEntry;
  confirm?: ConfirmMappingResult;
  line?: string;
};

type PendingTypeFile = { file: File; key: string };

const needsUserChoice = (p: MappingPreviewEntry): boolean => p.mapping_state === 'needs_user';

const buildConfirmItems = (
  previews: MappingPreviewEntry[],
  choiceMap: Record<number, Choice>,
): { items: ConfirmMappingItem[] } | { error: string } => {
  const items: ConfirmMappingItem[] = [];
  for (const p of previews) {
    if (p.mapping_state === 'failed' || p.mapping_state === 'not_found') {
      continue;
    }
    if (p.mapping_state === 'ready') {
      items.push({ artifact_id: p.artifact_id, target_company_id: 0, confirmed_label: '' });
      continue;
    }
    if (p.mapping_state === 'needs_user') {
      const ch = choiceMap[p.artifact_id];
      if (!ch?.companyId || ch.companyId <= 0) {
        return { error: __('Sélectionnez un dossier existant pour chaque fichier concerné.') };
      }
      items.push({ artifact_id: p.artifact_id, target_company_id: ch.companyId, confirmed_label: '' });
    }
  }
  if (items.length === 0) {
    return { error: __('Aucun fichier ne peut être importé (tous en échec à l’aperçu).') };
  }
  return { items };
};

const CabinetFileImportWizard = ({
  open,
  files,
  bootstrapKey,
  onClose,
  onImported,
  variant = 'modal',
  search,
  patchPortalSearch,
}: CabinetFileImportWizardProps) => {
  const [step, setStep] = useState<Step>('upload');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState<UploadedRow[]>([]);
  const [previews, setPreviews] = useState<MappingPreviewEntry[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [choices, setChoices] = useState<Record<number, Choice>>({});
  const [job, setJob] = useState<FileExtractionJobPoll | null>(null);
  const [perFile, setPerFile] = useState<PerFileStatus[]>([]);
  const [pendingTypeFiles, setPendingTypeFiles] = useState<PendingTypeFile[]>([]);
  const [typeChoices, setTypeChoices] = useState<Record<string, string>>({});
  const partialUploadedRef = useRef<UploadedRow[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const runTokenRef = useRef(0);
  const stepRef = useRef<Step>('upload');

  stepRef.current = step;

  const filesKey = useMemo(
    () => files.map(f => `${f.name}:${f.size}:${f.lastModified}`).join('|'),
    [files],
  );

  const fecDsnImportGate = useMemo(() => {
    try {
      return getFecDsnImportGateFromBootstrap(getBootstrapData(bootstrapKey));
    } catch {
      return getFecDsnImportGateFromBootstrap({ isFecDsnImportEnabled: false });
    }
  }, [bootstrapKey]);
  const fecDsnImportDisabled = !fecDsnImportGate.allowed;

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const mergePerFileConfirm = useCallback((results: ConfirmMappingResult[], uploadList: UploadedRow[]) => {
    setPerFile(prev =>
      uploadList.map(row => {
        const c = results.find(r => r.artifact_id === row.artifactId);
        const prevRow = prev.find(p => p.artifactId === row.artifactId);
        return {
          fileName: row.fileName,
          artifactId: row.artifactId,
          preview: prevRow?.preview,
          confirm: c,
        };
      }),
    );
  }, []);

  const advanceStep = useCallback(
    (next: Step, url: 'skip' | 'replace' | 'push') => {
      setStep(next);
      if (url === 'skip' || !patchPortalSearch || !open) {
        return;
      }
      const patchObj = serializeCabinetImportStepUrl(next);
      if (!patchObj) {
        return;
      }
      patchPortalSearch(patchObj, url === 'push' ? 'push' : 'replace');
    },
    [patchPortalSearch, open],
  );

  const startJobPoll = useCallback(
    (jobId: number) => {
      stopPoll();
      let consecutiveErrors = 0;
      const MAX_ERRORS = 3;

      const tick = async (): Promise<void> => {
        try {
          const j = await getFileExtractionJob(bootstrapKey, jobId);
          consecutiveErrors = 0;
          setJob(j);
          if (isJobTerminal(j)) {
            stopPoll();
            advanceStep('results', 'push');
            onImported?.();
          }
        } catch (e) {
          consecutiveErrors += 1;
          if (consecutiveErrors >= MAX_ERRORS) {
            stopPoll();
            const msg = e instanceof Error ? e.message : String(e);
            setErrorMessage(msg);
            advanceStep('error', 'push');
          }
        }
      };
      void tick();
      pollRef.current = setInterval(() => {
        void tick();
      }, 2000);
    },
    [advanceStep, bootstrapKey, onImported, stopPoll],
  );

  const proceedAfterUploads = useCallback(
    async (list: UploadedRow[], token: number): Promise<void> => {
      const ids = list.map(r => r.artifactId);
      const { previews: pv } = await postInboxPreview(bootstrapKey, ids);
      if (token !== runTokenRef.current) {
        return;
      }
      setPreviews(pv);

      const initial: PerFileStatus[] = list.map(row => {
        const preview = pv.find(p => p.artifact_id === row.artifactId);
        return { fileName: row.fileName, artifactId: row.artifactId, preview };
      });
      setPerFile(initial);

      const needMap = pv.some(needsUserChoice);
      if (needMap) {
        const res = await fetchCompanies({ page: 1, per_page: 100 }, bootstrapKey);
        if (token !== runTokenRef.current) {
          return;
        }
        setCompanies(res.companies);
        const nextChoices: Record<number, Choice> = {};
        for (const p of pv) {
          if (needsUserChoice(p)) {
            nextChoices[p.artifact_id] = {};
          }
        }
        setChoices(nextChoices);
        advanceStep('mapping', 'push');
      } else {
        setStep('confirming');
        const built = buildConfirmItems(pv, {});
        if ('error' in built) {
          setErrorMessage(built.error);
          advanceStep('error', 'push');
          return;
        }
        const { results } = await postConfirmMapping(bootstrapKey, built.items);
        if (token !== runTokenRef.current) {
          return;
        }
        mergePerFileConfirm(results, list);
        const mappedIds = results.filter(r => r.outcome === 'mapped').map(r => r.artifact_id);
        if (mappedIds.length === 0) {
          advanceStep('results', 'push');
          return;
        }
        advanceStep('job', 'push');
        const { job_id: newJobId } = await createFileExtractionJob(bootstrapKey, mappedIds);
        if (token !== runTokenRef.current) {
          return;
        }
        const first = await getFileExtractionJob(bootstrapKey, newJobId);
        setJob(first);
        if (isJobTerminal(first)) {
          advanceStep('results', 'push');
          onImported?.();
        } else {
          startJobPoll(newJobId);
        }
      }
    },
    [advanceStep, bootstrapKey, mergePerFileConfirm, onImported, startJobPoll],
  );

  useEffect(() => {
    if (!open) {
      runTokenRef.current += 1;
      setStep('upload');
      setErrorMessage(null);
      setUploaded([]);
      setPreviews([]);
      setChoices({});
      setJob(null);
      setPerFile([]);
      setPendingTypeFiles([]);
      setTypeChoices({});
      partialUploadedRef.current = [];
      stopPoll();
    }
  }, [open, stopPoll]);

  useEffect(() => {
    if (!open || files.length === 0 || fecDsnImportDisabled) {
      return;
    }

    const selectedFiles = files;
    const token = ++runTokenRef.current;
    let cancelled = false;

    const fail = (msg: string) => {
      if (cancelled || token !== runTokenRef.current) {
        return;
      }
      setErrorMessage(msg);
      advanceStep('error', 'push');
    };

    const runUploadAndPreview = async (): Promise<void> => {
      advanceStep('upload', 'replace');
      setErrorMessage(null);
      try {
        const list: UploadedRow[] = [];
        const pending: PendingTypeFile[] = [];

        for (const file of selectedFiles) {
          const key = `${file.name}:${file.size}:${file.lastModified}`;
          try {
            const { artifact } = await uploadInboxArtifact(bootstrapKey, file);
            if (cancelled || token !== runTokenRef.current) {
              return;
            }
            list.push({ fileName: file.name, artifactId: artifact.artifact_id });
          } catch (e) {
            if (e instanceof ArtifactTypeRequiredError) {
              pending.push({ file, key });
            } else {
              throw e;
            }
          }
        }

        if (cancelled || token !== runTokenRef.current) {
          return;
        }

        partialUploadedRef.current = list;
        setUploaded(list);

        if (pending.length > 0) {
          const initialTypes: Record<string, string> = {};
          for (const p of pending) {
            initialTypes[p.key] = CABINET_INBOX_ARTIFACT_TYPES[0];
          }
          setTypeChoices(initialTypes);
          setPendingTypeFiles(pending);
          advanceStep('type_select', 'push');
          return;
        }

        await proceedAfterUploads(list, token);
      } catch (e) {
        if (cancelled || token !== runTokenRef.current) {
          return;
        }
        const msg = e instanceof Error ? e.message : String(e);
        if (msg === 'SESSION_EXPIRED') {
          fail(__('Session expirée. Rechargez la page.'));
        } else {
          fail(msg);
        }
      }
    };

    void runUploadAndPreview();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filesKey is the stable fingerprint of `files`
  }, [advanceStep, bootstrapKey, fecDsnImportDisabled, filesKey, open, proceedAfterUploads]);

  useEffect(() => {
    return () => {
      stopPoll();
    };
  }, [stopPoll]);

  useEffect(() => {
    if (!open || !patchPortalSearch || files.length === 0) {
      return;
    }
    const qs = parsePortalSearchParams(search ?? '');
    if (qs.get('cabinetImport') !== '1') {
      return;
    }

    const rawStep = qs.get('cabinetImportStep');
    let targetStep: Step;
    if (rawStep === null || rawStep === '') {
      targetStep = 'upload';
    } else {
      const decoded = decodeCabinetImportStepToken(rawStep);
      if (decoded === null) {
        patchPortalSearch(
          serializeCabinetImportStepUrl(stepRef.current) ?? { cabinetImportStep: null },
          'replace',
        );
        return;
      }
      targetStep = decoded;
    }

    const cur = stepRef.current;
    if (cur === 'confirming') {
      return;
    }
    if (cur === targetStep) {
      return;
    }

    const canHydrateToStep =
      (): boolean => {
        switch (targetStep) {
          case 'upload':
            return (
              pendingTypeFiles.length === 0 &&
              previews.length === 0 &&
              job === null &&
              perFile.length === 0 &&
              uploaded.length === 0
            );
          case 'type_select':
            return pendingTypeFiles.length > 0;
          case 'mapping':
            return previews.some(needsUserChoice) && job === null;
          case 'job':
            return job !== null;
          case 'results':
            return perFile.length > 0;
          case 'error':
            return errorMessage !== null && errorMessage !== '';
          default:
            return false;
        }
      };

    if (!canHydrateToStep()) {
      return;
    }

    setStep(targetStep);
  }, [
    errorMessage,
    files.length,
    job,
    open,
    patchPortalSearch,
    pendingTypeFiles.length,
    perFile.length,
    previews,
    search,
    uploaded.length,
  ]);

  const handleTypeSelectContinue = async () => {
    const token = runTokenRef.current;
    setErrorMessage(null);
    setStep('upload');
    try {
      const newRows: UploadedRow[] = [];
      for (const p of pendingTypeFiles) {
        const t = typeChoices[p.key] ?? CABINET_INBOX_ARTIFACT_TYPES[0];
        const { artifact } = await uploadInboxArtifact(bootstrapKey, p.file, t);
        newRows.push({ fileName: p.file.name, artifactId: artifact.artifact_id });
      }
      if (token !== runTokenRef.current) {
        return;
      }
      const merged = [...partialUploadedRef.current, ...newRows];
      partialUploadedRef.current = merged;
      setUploaded(merged);
      setPendingTypeFiles([]);
      await proceedAfterUploads(merged, token);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg === 'SESSION_EXPIRED') {
        setErrorMessage(__('Session expirée. Rechargez la page.'));
      } else {
        setErrorMessage(msg);
      }
      advanceStep('error', 'push');
    }
  };

  const handleMappingContinue = async () => {
    const built = buildConfirmItems(previews, choices);
    if ('error' in built) {
      setErrorMessage(built.error);
      return;
    }
    setErrorMessage(null);
    setStep('confirming');
    try {
      const { results } = await postConfirmMapping(bootstrapKey, built.items);
      mergePerFileConfirm(results, uploaded);
      const mappedIds = results.filter(r => r.outcome === 'mapped').map(r => r.artifact_id);
      if (mappedIds.length === 0) {
        advanceStep('results', 'push');
        return;
      }
      advanceStep('job', 'push');
      const { job_id: newJobId } = await createFileExtractionJob(bootstrapKey, mappedIds);
      const first = await getFileExtractionJob(bootstrapKey, newJobId);
      setJob(first);
      if (isJobTerminal(first)) {
        advanceStep('results', 'push');
        onImported?.();
      } else {
        startJobPoll(newJobId);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg === 'SESSION_EXPIRED') {
        setErrorMessage(__('Session expirée. Rechargez la page.'));
      } else {
        setErrorMessage(msg);
      }
      advanceStep('error', 'push');
    }
  };

  const updateChoice = (artifactId: number, companyId: number | undefined) => {
    setChoices(prev => ({
      ...prev,
      [artifactId]: { companyId },
    }));
  };

  const updateTypeChoice = (key: string, value: string) => {
    setTypeChoices(prev => ({ ...prev, [key]: value }));
  };

  if (!open) {
    return null;
  }

  const panel = (
    <div className="cabinet-import-wizard-panel">
        <h3 id="cabinet-import-wizard-title">{__('Import de fichiers')}</h3>
        <FecDsnUploadResponsibilityNotice />
        <FeatureGateNotice decision={fecDsnImportGate} />

        {fecDsnImportDisabled ? (
          <div className="form-actions" style={{ marginTop: '1rem' }}>
            <button type="button" className="btn-primary" onClick={onClose}>
              {__('Fermer')}
            </button>
          </div>
        ) : (
          <>
        {step === 'upload' && (
          <p>{__('Téléversement et analyse des fichiers…')}</p>
        )}

        {step === 'type_select' && (
          <>
            <p>
              {__(
                'Le type de fichier n’a pas pu être détecté automatiquement pour un ou plusieurs fichiers. Choisissez le type pour chaque fichier.',
              )}
            </p>
            {pendingTypeFiles.map(p => (
              <div key={p.key} className="cabinet-import-wizard-row">
                <p>
                  <strong>{p.file.name}</strong>
                </p>
                <div className="form-field">
                  <label htmlFor={`artifact-type-${p.key}`}>{__('Type de fichier')}</label>
                  <select
                    id={`artifact-type-${p.key}`}
                    value={typeChoices[p.key] ?? CABINET_INBOX_ARTIFACT_TYPES[0]}
                    onChange={e => updateTypeChoice(p.key, e.target.value)}
                  >
                    {CABINET_INBOX_ARTIFACT_TYPES.map(v => (
                      <option key={v} value={v}>
                        {v === 'fec_fr' ? __('FEC (France)') : v === 'dsn_fr' ? __('DSN (France)') : v}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
            {errorMessage ? (
              <div className="notice notice-error">
                <p>{errorMessage}</p>
              </div>
            ) : null}
            <div className="form-actions" style={{ marginTop: '1rem' }}>
              <button type="button" className="btn-secondary" onClick={onClose}>
                {__('Annuler')}
              </button>
              <button type="button" className="btn-primary" onClick={() => void handleTypeSelectContinue()}>
                {__('Continuer')}
              </button>
            </div>
          </>
        )}

        {step === 'mapping' && (
          <>
            <p>
              {__(
                'Certains fichiers nécessitent votre choix de dossier. La création d’un nouveau dossier depuis un fichier import est désactivée — sélectionnez un dossier existant ou créez le dossier à la main, puis relancez l’import.',
              )}
            </p>
            {companies.length >= 100 ? (
              <p className="notice notice-warning">
                {__(
                  'Seuls les 100 premiers dossiers sont affichés. Si le vôtre n’apparaît pas, utilisez la liste complète des dossiers ou créez l’entreprise manuellement avant d’importer.',
                )}
              </p>
            ) : null}
            {previews.filter(needsUserChoice).map(p => (
              <div key={p.artifact_id} className="cabinet-import-wizard-row">
                <p>
                  <strong>{uploaded.find(u => u.artifactId === p.artifact_id)?.fileName ?? p.artifact_id}</strong>
                  {p.reason ? ` — ${p.reason}` : ''}
                </p>
                <div className="form-field">
                  <label htmlFor={`company-${p.artifact_id}`}>{__('Dossier')}</label>
                  <select
                    id={`company-${p.artifact_id}`}
                    value={choices[p.artifact_id]?.companyId ?? ''}
                    onChange={e => updateChoice(p.artifact_id, Number(e.target.value) || undefined)}
                  >
                    <option value="">{__('— Choisir —')}</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.siren})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
            {errorMessage ? (
              <div className="notice notice-error">
                <p>{errorMessage}</p>
              </div>
            ) : null}
            <div className="form-actions" style={{ marginTop: '1rem' }}>
              <button type="button" className="btn-secondary" onClick={onClose}>
                {__('Annuler')}
              </button>
              <button type="button" className="btn-primary" onClick={() => void handleMappingContinue()}>
                {__('Continuer')}
              </button>
            </div>
          </>
        )}

        {step === 'confirming' && <p>{__('Enregistrement du rattachement…')}</p>}

        {step === 'job' && (
          <>
            <p>{__('Import en cours…')}</p>
            {job ? (
              <p>
                {job.done_items}/{job.total_items} — {job.status}
              </p>
            ) : (
              <p>{__('Démarrage…')}</p>
            )}
          </>
        )}

        {step === 'results' && (
          <>
            <p>{__('Résultat')}</p>
            <ul className="cabinet-import-wizard-results">
              {perFile.map(row => {
                const line =
                  row.confirm?.outcome === 'error'
                    ? row.confirm.error_message ?? row.confirm.error_code ?? __('Erreur')
                    : row.preview?.mapping_state === 'failed'
                      ? row.preview.reason ?? __('Échec')
                      : row.confirm?.outcome === 'mapped'
                        ? __('Rattaché')
                        : row.confirm?.outcome ?? row.preview?.mapping_state ?? '—';
                return (
                  <li key={row.artifactId}>
                    <strong>{row.fileName}</strong>: {line}
                  </li>
                );
              })}
            </ul>
            {job && job.items.length > 0 ? (
              <details>
                <summary>{__('Détail technique')}</summary>
                <ul>
                  {job.items.map(it => (
                    <li key={it.id}>
                      {it.item_type} #{it.id}: {it.status}
                      {it.error?.message ? ` — ${it.error.message}` : ''}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
            <div className="form-actions">
              <button type="button" className="btn-primary" onClick={onClose}>
                {__('Fermer')}
              </button>
            </div>
          </>
        )}

        {step === 'error' && errorMessage && (
          <>
            <div className="notice notice-error">
              <p>{errorMessage}</p>
            </div>
            <div className="form-actions">
              <button type="button" className="btn-primary" onClick={onClose}>
                {__('Fermer')}
              </button>
            </div>
          </>
        )}
          </>
        )}
    </div>
  );

  if (variant === 'inline') {
    return (
      <div className="cabinet-import-wizard-inline form-container--wide mx-auto" aria-labelledby="cabinet-import-wizard-title">
        {panel}
      </div>
    );
  }

  return (
    <div
      className="cabinet-import-wizard-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cabinet-import-wizard-title"
    >
      {panel}
    </div>
  );
};

export default CabinetFileImportWizard;
